import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

const COLORS = [
  '#1D9E75', '#D85A30', '#3B82F6', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1', '#14B8A6', '#EF4444',
];

const money = (n, cur = 'USD') => {
  const v = (n ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 2 });
  return cur === 'USD' ? `$${v}` : `${v} ${cur}`;
};
const fmtDate = (s) => s ? new Date(s).toLocaleString('en-GB') : 'never';

export default function Stocks({ token, onLogout }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [cash, setCash] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const loadStatusAccountCash = () => {
    axios.get(`${API}/ibkr/status`, { headers }).then(r => setStatus(r.data)).catch(() => {});
    axios.get(`${API}/ibkr/account`, { headers }).then(r => setAccount(r.data)).catch(() => {});
    axios.get(`${API}/ibkr/cash`, { headers }).then(r => setCash(r.data)).catch(() => {});
  };

  // /ibkr/positions merges live Yahoo prices server-side
  const loadPositions = () => axios.get(`${API}/ibkr/positions`, { headers }).then(r => setPositions(r.data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
    loadStatusAccountCash();
    loadPositions();   // auto-fetch live prices on page load
  }, []);

  // "Sync Positions" — triggers the Flex sync (~30s), then reloads everything
  const syncPositions = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/ibkr/sync`, {}, { headers });
      if (!r.data.ok) {
        alert('Sync ran but reported an issue:\n\n' + (r.data.output || r.data.error || 'Unknown error'));
      }
      loadStatusAccountCash();
      await loadPositions();
    } catch (err) {
      alert('Sync failed. The Flex service may be briefly throttled — try again in a minute.');
    } finally {
      setSyncing(false);
    }
  };

  // "Refresh Prices" — instant; re-fetches positions (live Yahoo prices)
  const refreshPrices = async () => {
    setRefreshing(true);
    try {
      await loadPositions();
    } finally {
      setRefreshing(false);
    }
  };

  const hasData = !!status?.last_sync;

  const pieData = positions
    .filter(p => (p.market_value || 0) > 0)
    .map(p => ({ name: p.symbol || p.description || '—', value: p.market_value || 0 }));

  const Card = ({ label, value, accent }) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-gray-900'}`}>{value}</p>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">
        <Link to="/investments" className="text-sm text-gray-500 hover:text-green-600">← Back to Overview</Link>

        <div className="flex items-center gap-3 mt-3 mb-6">
          <div className="text-3xl">📈</div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stocks — IBKR</h1>
            <p className="text-gray-500 text-sm">Interactive Brokers via Flex Web Service</p>
          </div>
        </div>

        {/* Status bar */}
        <div className="rounded-2xl p-4 mb-6 bg-white border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm">
            <p className="text-gray-700">
              <span className="font-medium">Last positions sync:</span> {fmtDate(status?.last_sync)}
            </p>
            <p className="text-gray-500 flex items-center gap-1 mt-0.5">
              <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
              Prices: Live via Yahoo Finance
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={refreshPrices} disabled={refreshing}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50">
              {refreshing ? 'Refreshing…' : '🔄 Refresh Prices'}
            </button>
            <button onClick={syncPositions} disabled={syncing}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50">
              {syncing ? 'Syncing… (~30s)' : '🔄 Sync Positions'}
            </button>
          </div>
        </div>

        {!hasData ? (
          /* Empty state — no sync yet */
          <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100 text-center">
            <div className="text-6xl mb-4">📊💼</div>
            <h2 className="text-lg font-semibold text-gray-900">No data synced yet</h2>
            <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
              Click <span className="font-medium">Sync Positions</span> to pull your latest holdings, cash and trades
              from IBKR via the Flex Web Service. It takes about 30 seconds while IBKR generates the report.
            </p>
            <p className="text-xs text-gray-400 mt-3">Positions then sync automatically every day at 9 AM Dubai time.</p>
          </div>
        ) : (
          <>
            {/* Account summary cards (base currency) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card label="Net Liquidation" value={money(account?.net_liquidation)} />
              <Card label="Total Cash" value={money(account?.total_cash)} />
              <Card label="Unrealized P&L" value={`${(account?.unrealized_pnl ?? 0) >= 0 ? '+' : ''}${money(account?.unrealized_pnl)}`}
                accent={(account?.unrealized_pnl ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'} />
              <Card label="Realized P&L" value={`${(account?.realized_pnl ?? 0) >= 0 ? '+' : ''}${money(account?.realized_pnl)}`}
                accent={(account?.realized_pnl ?? 0) >= 0 ? 'text-green-500' : 'text-red-500'} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Positions table */}
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <h3 className="text-base font-semibold text-gray-900 p-4 border-b border-gray-100">Positions</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Symbol</th>
                        <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Description</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Avg Cost</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Price</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Mkt Value</th>
                        <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Unreal. P&L</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.length === 0 && (
                        <tr><td colSpan={7} className="text-center py-10 text-gray-400 text-sm">
                          No open positions — account is currently in cash.
                        </td></tr>
                      )}
                      {positions.map(p => {
                        const pnl = p.unrealized_pnl || 0;
                        const pct = p.unrealized_pnl_pct;
                        const cur = p.currency || 'USD';
                        return (
                          <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="p-3 text-sm font-medium text-gray-900">
                              {p.symbol}
                              {!p.live && <span className="ml-1 text-[10px] text-amber-500" title="No live price; showing cost">·est</span>}
                            </td>
                            <td className="p-3 text-sm text-gray-500 hidden md:table-cell">{p.description}</td>
                            <td className="p-3 text-sm text-gray-700 text-right">{p.position}</td>
                            <td className="p-3 text-sm text-gray-500 text-right hidden md:table-cell">{money(p.avg_cost, cur)}</td>
                            <td className="p-3 text-sm text-gray-700 text-right">
                              {money(p.market_price, cur)}
                              {p.change_percent != null && (
                                <span className={`block text-[11px] ${p.change_percent >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                  {p.change_percent >= 0 ? '+' : ''}{p.change_percent}%
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-sm font-semibold text-gray-900 text-right">{money(p.market_value, cur)}</td>
                            <td className={`p-3 text-sm font-medium text-right ${pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {pnl >= 0 ? '+' : ''}{money(pnl, cur)}
                              {pct != null && <span className="block text-xs">{pct >= 0 ? '+' : ''}{pct.toFixed(1)}%</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Allocation pie */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <h3 className="text-base font-semibold text-gray-900 mb-2">Allocation</h3>
                {pieData.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-16">No open positions to chart.</p>
                ) : (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={2} dataKey="value">
                          {pieData.map((entry, index) => (
                            <Cell key={index} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={v => money(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="mt-3 space-y-1">
                      {pieData.map((d, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                            {d.name}
                          </span>
                          <span className="text-gray-500">{money(d.value)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Cash balances */}
            <div className="mb-6">
              <h3 className="text-base font-semibold text-gray-900 mb-3">Cash Balances</h3>
              {cash.length === 0 ? (
                <p className="text-gray-400 text-sm">No cash balances synced.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {cash.map(c => (
                    <div key={c.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                      <p className="text-xs text-gray-500">{c.currency}</p>
                      <p className="text-lg font-bold text-gray-900">{(c.balance ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 2 })}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
